/**
 * Quick check of a tool error message (a Jinja template) while it is typed on the Tool Errors page.
 * The API does the real check (jinja2 parse + undeclared variables, api-service tool_errors.message_problems);
 * this mirrors it closely enough to stop an obviously broken message before Save.
 */
const JINJA_WORDS = new Set(['and', 'or', 'not', 'if', 'else', 'elif', 'endif', 'in', 'is', 'none', 'true', 'false', 'loop']);

export type MessageProblem =
  | { kind: 'empty' }
  | { kind: 'unbalanced' }
  | { kind: 'unknown'; names: string[] };

/** Names a template reads: identifiers inside {{ }} / {% %} that are not attributes, filters, strings or keywords. */
export const templateNames = (message: string): string[] => {
  const names = new Set<string>();
  for (const block of message.match(/\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}/g) ?? []) {
    const body = block.slice(2, -2).replace(/(['"])(?:\\.|(?!\1).)*\1/g, ' ');
    for (const m of body.matchAll(/([.|]\s*)?\b([A-Za-z_][A-Za-z0-9_]*)\b/g)) {
      if (!m[1] && !JINJA_WORDS.has(m[2].toLowerCase())) names.add(m[2]);
    }
  }
  return [...names];
};

export const messageProblem = (message: string, variables: string[]): MessageProblem | null => {
  if (!message.trim()) return { kind: 'empty' };
  const count = (s: string) => message.split(s).length - 1;
  if (count('{{') !== count('}}') || count('{%') !== count('%}')) return { kind: 'unbalanced' };
  const unknown = templateNames(message).filter((n) => !variables.includes(n));
  return unknown.length ? { kind: 'unknown', names: unknown } : null;
};
